import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { UNSAVED_BEFORE_EMAIL_DOMAIN_TEXT, UNSAVED_BEFORE_STRIPE_TEXT } from "./chamberSettingsUnsavedText";
import { ApolloError } from "@apollo/client";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Loader from "../../../common/Loader";
import UnsavedChangesPrompt, {
  UnsavedChangesDialog,
} from "../../../common/UnsavedChangesPrompt";
import { ActionSuccessDialog } from "../../../common/dashboard/ActionSuccessDialog";
import { ConfirmDialog } from "../../../common/dashboard/ConfirmDialog";
import {
  ChamberMemberProPagePermissionsEnum,
  ChamberMemberProSettingsSectionEnum,
  UserType,
  useDeleteStripeConnectForSandboxChamberMutation,
  useGetChamberMemberProSettingsQuery,
  useGetCmpInternalEmailSettingsQuery,
  useRefreshChamberMemberProSenderDomainStatusForChamberMutation,
  useRefreshStripeConnectStatusForChamberMutation,
  useRemoveChamberMemberProSenderDomainForChamberMutation,
  useSendChamberMemberProSenderDomainTestEmailMutation,
  useStartStripeConnectForChamberMutation,
  useStartChamberMemberProSenderDomainVerificationMutation,
  useUpdateChamberMemberProSettingsForChamberMutation,
  useUpdateCmpInternalEmailSettingMutation,
} from "../../../../graphql/types";
import { getInvoiceCopyCards, getInvoiceCopyCardUpdates, withInvoiceCopyCardEmails } from "./tabs/internalEmailInvoiceCopies";
import useGetUserFromUserIdInAuth from "../../../../hooks/useGetUserFromUserIdInAuth";
import { GlobalSettingsSectionId, SettingsTabs, TabId } from "./common";
import { globalSettingsSectionIds } from "./common/types";
import {
  CHAMBER_GLOBALS_REQUIRED_MESSAGE,
  MULTIPLE_BILLING_DATES_REQUIRED_MESSAGE,
  RENEWAL_FIXED_DATE_REQUIRED_MESSAGE,
  areMultipleBillingDatesMissing,
  isRenewalFixedDateMissing,
  getMissingChamberGlobalsFields,
} from "./chamberGlobalsValidation";
import { tabItems } from "./common/SettingsTabs";
import {
  AffinityProgramSetupTab,
  EmailTab,
  GlobalSettingsTab,
  GovernanceGroupsTab,
  MembershipTab,
} from "./tabs";
import { ChamberSettingsFormState, EmailTemplate } from "./settingsData";
import {
  mapApiSettingsToFormState,
  mapFormStateToApiSettingsInput,
} from "./settingsMappers";
import { formatCmpLastSavedLabel, normalizeCmpTimeZone } from "../../../../utils/cmpTimeZones";
import { UserTypeContext } from "../../../../contexts/UserTypeContext";
import { useCmpOrganizationNoun } from "../../../../contexts/CmpOrganizationNounContext";
import { applyCmpOrganizationNounToPageName } from "../../../../utils/cmpOrganizationNoun";
import { usePageHeaderOverride } from "../../../Layout/PageContainer";
import { UsersPermissionsTab } from "./usersPermissions";
import { StaffUserCreationNavigationState } from "./usersPermissions/staffUserCreationFeedback";
import { hasUnsavedFormChanges } from "../../../common/formStateDirty";
import { shouldApplySettingsQueryRefresh } from "./settingsQueryRefresh";
import { getSettingsSectionForSave } from "./settingsSectionRouting";
import { getMembershipEmployeeRangeIssues } from "./settingsMembershipRangeValidation";
import {
  CALLER_HANDLED_USER_ERROR_CONTEXT,
  getPreserveExpectedErrorsForCallerContext,
} from "../../../../utils/apolloErrorPolicy";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
  STAFF_SUPPORT_EMAIL,
  UserFacingError,
} from "../../../../utils/userFacingError";

const CHAMBER_SET_UP_UNSAVED_MESSAGE =
  "Click Save & Update before leaving or changes will be lost.";

const settingsOperationErrorContext = getPreserveExpectedErrorsForCallerContext({
  expectedGraphQLErrorCodes: ["BAD_USER_INPUT"],
});

// Stripe Connect and email domain calls show their error in their section
// instead of opening the full-page error (wave 1, Oct 19 2026).
const settingsActionOptions = { context: CALLER_HANDLED_USER_ERROR_CONTEXT };

export const SECTION_SAVE_FAILED_MESSAGE =
  "Your changes were not saved. Check the message in this section, then try again.";

const isRecoverableSettingsNetworkError = (error: ApolloError): boolean => {
  const networkError = error.networkError;
  if (
    !networkError ||
    error.graphQLErrors.length > 0 ||
    error.protocolErrors.length > 0 ||
    error.clientErrors.length > 0
  ) {
    return false;
  }

  // A non-2xx response can contain GraphQL access or tenant errors. It must
  // still block the form even when Apollo classifies it as a network error.
  if (
    "result" in networkError &&
    typeof networkError.result === "object" &&
    networkError.result !== null &&
    Object.prototype.hasOwnProperty.call(networkError.result, "errors")
  ) {
    return false;
  }

  // Keep a previously loaded draft only for transport or server outages.
  // In particular, HTTP client errors (including 401/403) must stay blocking.
  return !("statusCode" in networkError) || networkError.statusCode >= 500;
};

const getInitialSettingsTab = (searchParams: URLSearchParams): TabId => {
  const requestedTab = searchParams.get("tab");

  return tabItems.some(({ id }) => id === requestedTab)
    ? (requestedTab as TabId)
    : "globalSettings";
};

const getSettingsSectionForTab = (
  tabId: TabId
): ChamberMemberProSettingsSectionEnum | undefined => {
  switch (tabId) {
    case "globalSettings":
      return ChamberMemberProSettingsSectionEnum.GlobalSettings;
    case "membershipSettings":
      return ChamberMemberProSettingsSectionEnum.MembershipSettings;
    case "emailCommunications":
      return ChamberMemberProSettingsSectionEnum.EmailAndCommunicationSettings;
    case "governanceGroups":
      return ChamberMemberProSettingsSectionEnum.GovernanceSettings;
    default:
      return undefined;
  }
};

const formatSavedAt = (savedAtUtcMilli: number | null | undefined, timeZone: string | null | undefined) =>
  savedAtUtcMilli ? formatCmpLastSavedLabel(savedAtUtcMilli, timeZone) : "Loaded defaults";

const getChamberGlobalsSetup = (
  setup: ChamberSettingsFormState["setup"]
): Omit<
  ChamberSettingsFormState["setup"],
  "membershipApplicationCommunicationOptions"
> => ({
  fiscalYearStartMonth: setup.fiscalYearStartMonth,
  fiscalYearStartDay: setup.fiscalYearStartDay,
  currency: setup.currency,
  timeZone: setup.timeZone,
  membershipRenewalFixedDateEnabled:
    setup.membershipRenewalFixedDateEnabled,
  membershipRenewalFixedDateMonth: setup.membershipRenewalFixedDateMonth,
  membershipRenewalFixedDateDay: setup.membershipRenewalFixedDateDay,
  ...(setup.multipleBillingDates
    ? { multipleBillingDates: setup.multipleBillingDates }
    : {}),
});

const getSectionScopedSettings = (
  baseSettings: ChamberSettingsFormState,
  sourceSettings: ChamberSettingsFormState,
  sectionId: GlobalSettingsSectionId,
  templateId?: string
): ChamberSettingsFormState => {
  const baseWithSavedTimestamp = {
    ...baseSettings,
    updatedAtUtcMilli: sourceSettings.updatedAtUtcMilli,
  };

  switch (sectionId) {
    case "chamberGlobals":
      return {
        ...baseWithSavedTimestamp,
        setup: {
          ...baseWithSavedTimestamp.setup,
          ...getChamberGlobalsSetup(sourceSettings.setup),
        },
        membership: {
          ...baseWithSavedTimestamp.membership,
          globalToggleState: {
            ...baseWithSavedTimestamp.membership.globalToggleState,
            prorate: sourceSettings.membership.globalToggleState.prorate,
          },
        },
        paymentsAndTaxes: {
          ...baseWithSavedTimestamp.paymentsAndTaxes,
          provincialChamberFee:
            sourceSettings.paymentsAndTaxes.provincialChamberFee,
        },
      };
    case "taxSettings":
      return {
        ...baseWithSavedTimestamp,
        paymentsAndTaxes: {
          ...baseWithSavedTimestamp.paymentsAndTaxes,
          taxRegistrationNumber:
            sourceSettings.paymentsAndTaxes.taxRegistrationNumber,
          invoiceTaxSettings: sourceSettings.paymentsAndTaxes.invoiceTaxSettings,
        },
      };
    case "accountAllocation":
      return {
        ...baseWithSavedTimestamp,
        paymentsAndTaxes: {
          ...baseWithSavedTimestamp.paymentsAndTaxes,
          accountAllocations:
            sourceSettings.paymentsAndTaxes.accountAllocations,
        },
      };
    case "paymentOptions":
      return {
        ...baseWithSavedTimestamp,
        paymentsAndTaxes: {
          ...baseWithSavedTimestamp.paymentsAndTaxes,
          manualPaymentMethods:
            sourceSettings.paymentsAndTaxes.manualPaymentMethods,
          stripeConnect: sourceSettings.paymentsAndTaxes.stripeConnect,
          cardProcessingFee: sourceSettings.paymentsAndTaxes.cardProcessingFee,
          monthlyMembershipAdministrativeFee:
            sourceSettings.paymentsAndTaxes.monthlyMembershipAdministrativeFee,
          eventTicketCreditCardPaymentMode:
            sourceSettings.paymentsAndTaxes.eventTicketCreditCardPaymentMode,
          chamberGroupInsurancePayment:
            sourceSettings.paymentsAndTaxes.chamberGroupInsurancePayment,
        },
      };
    case "invoicingOptions":
      return {
        ...baseWithSavedTimestamp,
        paymentsAndTaxes: {
          ...baseWithSavedTimestamp.paymentsAndTaxes,
          eTransferEmail: sourceSettings.paymentsAndTaxes.eTransferEmail,
          invoiceStartNumber:
            sourceSettings.paymentsAndTaxes.invoiceStartNumber,
          membershipDiscount:
            sourceSettings.paymentsAndTaxes.membershipDiscount,
          paidInvoiceReceiptCopy:
            sourceSettings.paymentsAndTaxes.paidInvoiceReceiptCopy,
          createdInvoiceCopy:
            sourceSettings.paymentsAndTaxes.createdInvoiceCopy,
          oneTimeOnboardingFee:
            sourceSettings.paymentsAndTaxes.oneTimeOnboardingFee,
          invoicePaymentTerms:
            sourceSettings.paymentsAndTaxes.invoicePaymentTerms,
          overdueInvoiceLateFeePercentage:
            sourceSettings.paymentsAndTaxes.overdueInvoiceLateFeePercentage,
          overdueInvoiceLateFeeAmountInDollars:
            sourceSettings.paymentsAndTaxes.overdueInvoiceLateFeeAmountInDollars,
          overdueInvoiceLateFeeEnabled:
            sourceSettings.paymentsAndTaxes.overdueInvoiceLateFeeEnabled,
          invoiceFooterNotesEnabled:
            sourceSettings.paymentsAndTaxes.invoiceFooterNotesEnabled,
          invoiceBillingContact:
            sourceSettings.paymentsAndTaxes.invoiceBillingContact,
        },
      };
    case "memberCommunication":
      return {
        ...baseWithSavedTimestamp,
        setup: {
          ...baseWithSavedTimestamp.setup,
          membershipApplicationCommunicationOptions:
            sourceSettings.setup.membershipApplicationCommunicationOptions,
        },
      };
    case "membershipSettings":
      return {
        ...baseWithSavedTimestamp,
        membership: sourceSettings.membership,
      };
    case "emailCommunications":
      return {
        ...baseWithSavedTimestamp,
        email: templateId ? {
          ...baseWithSavedTimestamp.email,
          enabledSenderEmails: Array.from(new Set([...baseWithSavedTimestamp.email.enabledSenderEmails,
            ...(sourceSettings.email.emailTemplates.find(template => template.id === templateId)?.senderEmail
              ? [sourceSettings.email.emailTemplates.find(template => template.id === templateId)!.senderEmail!] : [])])),
          emailTemplates: baseWithSavedTimestamp.email.emailTemplates.map(template => template.id === templateId
            ? sourceSettings.email.emailTemplates.find(candidate => candidate.id === templateId) || template : template),
        } : sourceSettings.email,
      };
    case "governanceGroups":
      return {
        ...baseWithSavedTimestamp,
        governance: sourceSettings.governance,
      };
    default:
      return baseWithSavedTimestamp;
  }
};

const getSectionValue = (
  settings: ChamberSettingsFormState,
  sectionId: GlobalSettingsSectionId
) => {
  switch (sectionId) {
    case "chamberGlobals":
      return {
        setup: getChamberGlobalsSetup(settings.setup),
        prorate: settings.membership.globalToggleState.prorate,
        provincialChamberFee: settings.paymentsAndTaxes.provincialChamberFee,
      };
    case "taxSettings":
      return {
        taxRegistrationNumber: settings.paymentsAndTaxes.taxRegistrationNumber,
        invoiceTaxSettings: settings.paymentsAndTaxes.invoiceTaxSettings,
      };
    case "accountAllocation":
      return settings.paymentsAndTaxes.accountAllocations;
    case "paymentOptions":
      return {
        manualPaymentMethods: settings.paymentsAndTaxes.manualPaymentMethods,
        stripeConnect: settings.paymentsAndTaxes.stripeConnect,
        cardProcessingFee: settings.paymentsAndTaxes.cardProcessingFee,
        monthlyMembershipAdministrativeFee:
          settings.paymentsAndTaxes.monthlyMembershipAdministrativeFee,
        eventTicketCreditCardPaymentMode:
          settings.paymentsAndTaxes.eventTicketCreditCardPaymentMode,
        chamberGroupInsurancePayment:
          settings.paymentsAndTaxes.chamberGroupInsurancePayment,
      };
    case "invoicingOptions":
      return {
        eTransferEmail: settings.paymentsAndTaxes.eTransferEmail,
        invoiceStartNumber: settings.paymentsAndTaxes.invoiceStartNumber,
        membershipDiscount: settings.paymentsAndTaxes.membershipDiscount,
        paidInvoiceReceiptCopy: settings.paymentsAndTaxes.paidInvoiceReceiptCopy,
        createdInvoiceCopy: settings.paymentsAndTaxes.createdInvoiceCopy,
        oneTimeOnboardingFee:
          settings.paymentsAndTaxes.oneTimeOnboardingFee,
        invoicePaymentTerms: settings.paymentsAndTaxes.invoicePaymentTerms,
        overdueInvoiceLateFeePercentage:
          settings.paymentsAndTaxes.overdueInvoiceLateFeePercentage,
        overdueInvoiceLateFeeAmountInDollars:
          settings.paymentsAndTaxes.overdueInvoiceLateFeeAmountInDollars,
        overdueInvoiceLateFeeEnabled:
          settings.paymentsAndTaxes.overdueInvoiceLateFeeEnabled,
        invoiceFooterNotesEnabled:
          settings.paymentsAndTaxes.invoiceFooterNotesEnabled,
        invoiceBillingContact: settings.paymentsAndTaxes.invoiceBillingContact,
      };
    case "memberCommunication":
      return settings.setup.membershipApplicationCommunicationOptions;
    case "membershipSettings":
      return settings.membership;
    case "emailCommunications":
      return settings.email;
    case "governanceGroups":
      return settings.governance;
    default:
      return null;
  }
};

const ChamberSettings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const noun = useCmpOrganizationNoun();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabId>(() =>
    getInitialSettingsTab(searchParams)
  );
  const [settings, setSettings] = useState<ChamberSettingsFormState | null>(
    null
  );
  const [lastSavedSettings, setLastSavedSettings] =
    useState<ChamberSettingsFormState | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [stripeConnectError, setStripeConnectError] = useState<string | null>(
    null
  );
  const [senderDomainError, setSenderDomainError] = useState<string | null>(
    null
  );
  const [statusNotice, setStatusNotice] = useState<string | null>(null);
  // One design pop-up per section save (D:13295). Inline section errors stay.
  const [sectionSaveFeedback, setSectionSaveFeedback] = useState<{
    message: string;
    tone: "success" | "error";
  } | null>(null);
  const [savedAtUtcMilli, setSavedAtUtcMilli] = useState<number | null>(null);
  const [activeSavingSection, setActiveSavingSection] =
    useState<GlobalSettingsSectionId | null>(null);
  const [lastSavedSection, setLastSavedSection] =
    useState<GlobalSettingsSectionId | null>(null);
  const [lastFailedSection, setLastFailedSection] =
    useState<GlobalSettingsSectionId | null>(null);
  // POL-026(b): destructive confirms ride the shared ConfirmDialog instead of
  // native window.confirm (copy preserved verbatim, mutation loading wired).
  const [stripeDisconnectConfirmOpen, setStripeDisconnectConfirmOpen] =
    useState(false);
  const [removeSenderDomainConfirmOpen, setRemoveSenderDomainConfirmOpen] =
    useState(false);
  // B15 guardrail: the Affinity tab manages its own drafts outside the
  // settings form state; it reports dirtiness so the route guard covers it.
  const [affinityDirty, setAffinityDirty] = useState(false);
  const [pendingTab, setPendingTab] = useState<TabId | null>(null);
  const [affinityBusy, setAffinityBusy] = useState(false);
  const [governanceDraftDirty, setGovernanceDraftDirty] = useState(false);
  const [emailTemplateEditorDirty, setEmailTemplateEditorDirty] = useState(false);
  const [internalEmailsDirty, setInternalEmailsDirty] = useState(false);
  const [emailSubscriberTargetDirty, setEmailSubscriberTargetDirty] =
    useState(false);
  const [emailSubscriberTargetBusy, setEmailSubscriberTargetBusy] =
    useState(false);
  const lastSeenSettingsQueryRef = useRef<{
    chamberId: string;
    settings: ChamberSettingsFormState;
  } | null>(null);
  const pendingSettingsQueryRef = useRef<{
    chamberId: string;
    settings: ChamberSettingsFormState;
  } | null>(null);
  const blockedSettingsChamberRef = useRef<string | null>(null);
  const handledCreationFeedbackRef = useRef<
    StaffUserCreationNavigationState["staffUserCreationFeedback"] | null
  >(null);
  const locationState =
    location.state as StaffUserCreationNavigationState | null;
  const staffUserCreationFeedback = locationState?.staffUserCreationFeedback;

  const {
    state: { userType },
  } = useContext(UserTypeContext);
  const loggedInUser = useGetUserFromUserIdInAuth();
  const chamberId = loggedInUser?.chamberId;
  // IF13 Q-A: the Global Settings invoice copy fields show the Internal Emails cards.
  const { data: internalEmailData, refetch: refetchInternalEmails } = useGetCmpInternalEmailSettingsQuery({
    variables: { chamberId: chamberId || "" },
    skip: !chamberId,
    fetchPolicy: "network-only",
    context: settingsOperationErrorContext,
  });
  const [updateInternalEmailSetting] = useUpdateCmpInternalEmailSettingMutation({ context: settingsOperationErrorContext });
  const invoiceCopyCards = useMemo(() => getInvoiceCopyCards(internalEmailData?.getCmpInternalEmailSettings), [internalEmailData]);
  useEffect(() => {
    if (!invoiceCopyCards || !lastSavedSettings || !settings) return;
    const baseline = lastSavedSettings.paymentsAndTaxes;
    const nextBaseline = withInvoiceCopyCardEmails(baseline, invoiceCopyCards);
    if (nextBaseline === baseline) return;
    const current = settings.paymentsAndTaxes;
    // A field that staff are typing in keeps the typed value.
    const keepTyped = (field: "paidInvoiceReceiptCopy" | "createdInvoiceCopy") =>
      current[field].email === baseline[field].email ? nextBaseline[field] : current[field];
    setLastSavedSettings({ ...lastSavedSettings, paymentsAndTaxes: nextBaseline });
    setSettings({ ...settings, paymentsAndTaxes: { ...current, paidInvoiceReceiptCopy: keepTyped("paidInvoiceReceiptCopy"), createdInvoiceCopy: keepTyped("createdInvoiceCopy") } });
  }, [invoiceCopyCards, lastSavedSettings, settings]);
  const setPageHeaderOverride = usePageHeaderOverride();
  const isOwner = userType === UserType.ChamberAdmin;
  const enabledPages = useMemo(
    () =>
      loggedInUser?.usagePermissions?.chamberMemberPro_EnabledPages || [],
    [loggedInUser?.usagePermissions?.chamberMemberPro_EnabledPages]
  );
  const visibleTabItems = useMemo(
    () =>
      isOwner
        ? tabItems
        : tabItems.filter(({ id }) => {
            switch (id) {
              case "globalSettings":
              case "governanceGroups":
                return enabledPages.includes(
                  ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberGlobalSettings
                );
              case "membershipSettings":
                return enabledPages.includes(
                  ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberMembershipSettings
                );
              case "emailCommunications":
                return enabledPages.includes(
                  ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberEmailAndCommunicationSettings
                );
              case "affinityProgramSetup":
                return enabledPages.includes(
                  ChamberMemberProPagePermissionsEnum.ChamberAdminMyChamberAffinityProgramSetup
                );
              case "usersPermissions":
                return enabledPages.includes(
                  ChamberMemberProPagePermissionsEnum.ChamberAdminViewUserActivityLog
                );
              default:
                return false;
            }
          }),
    [enabledPages, isOwner]
  );
  const visibleTabIds = useMemo(
    () => visibleTabItems.map(({ id }) => id),
    [visibleTabItems]
  );
  const activeTabIsVisible = visibleTabIds.includes(activeTab);
  const activeSettingsSection = getSettingsSectionForTab(activeTab);
  const shouldLoadSettings = Boolean(
    chamberId && activeTabIsVisible && activeSettingsSection
  );

  useEffect(() => {
    if (
      loggedInUser &&
      visibleTabItems.length > 0 &&
      !visibleTabItems.some(({ id }) => id === activeTab)
    ) {
      setActiveTab(visibleTabItems[0].id);
    }
  }, [activeTab, loggedInUser, visibleTabItems]);

  useEffect(() => {
    // Every tab, including Users and Permissions, keeps the route's
    // "Chamber Set Up" header (D:5219).
    // D@847394 (AE-13): one header style for every tab, so switching tabs
    // no longer changes the title size or moves the content.
    setPageHeaderOverride({
      titleClassName: "text-[28px] font-bold leading-[35px] text-[#0A0A0A]",
      descriptionClassName: "mt-[6px] text-[14px] leading-[18px] text-[#0A0A0A]",
      horizontalPaddingClassName: "px-[16px] md:px-[40px]",
      headerSpacingClassName: "pt-[21px] pb-0",
    });

    return () => setPageHeaderOverride(null);
  }, [setPageHeaderOverride]);

  useEffect(() => {
    if (
      !staffUserCreationFeedback ||
      handledCreationFeedbackRef.current === staffUserCreationFeedback
    ) {
      return;
    }

    // UsersPermissionsTab shows the one creation pop-up from its initial
    // feedback prop. Clear the route state so a refresh does not repeat it.
    handledCreationFeedbackRef.current = staffUserCreationFeedback;
    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: null,
    });
  }, [
    location.hash,
    location.pathname,
    location.search,
    navigate,
    staffUserCreationFeedback,
  ]);

  const handleTabChange = useCallback(
    (nextTab: TabId) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      setActiveTab(nextTab);

      if (nextTab === "globalSettings") {
        nextSearchParams.delete("tab");
      } else {
        nextSearchParams.set("tab", nextTab);
      }

      setSearchParams(nextSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const {
    data: settingsData,
    loading: settingsLoading,
    error: settingsError,
    refetch,
  } = useGetChamberMemberProSettingsQuery({
    variables: {
      chamberId: chamberId || "",
      section: isOwner ? undefined : activeSettingsSection,
    },
    skip: !shouldLoadSettings,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    context: settingsOperationErrorContext,
  });
  // Last Saved stamps use the chamber time zone (Oct 30 2026).
  const chamberTimeZone = settingsData?.getChamberMemberProSettings.setup.timeZone;

  useEffect(() => {
    if (!shouldLoadSettings || !chamberId) return;
    if (settingsError) {
      if (!isRecoverableSettingsNetworkError(settingsError)) {
        blockedSettingsChamberRef.current = chamberId;
      }
    } else if (!settingsLoading && settingsData?.getChamberMemberProSettings) {
      // A failed access/validation read stays blocking through a later outage.
      // Only a successful read can make this chamber's cached form usable again.
      blockedSettingsChamberRef.current = null;
    }
  }, [chamberId, settingsData, settingsError, settingsLoading, shouldLoadSettings]);

  const [updateChamberMemberProSettings, { loading: saving }] =
    useUpdateChamberMemberProSettingsForChamberMutation();
  const [startStripeConnectForChamber, { loading: startingStripeConnect }] =
    useStartStripeConnectForChamberMutation(settingsActionOptions);
  const [
    refreshStripeConnectStatusForChamber,
    { loading: refreshingStripeConnect },
  ] = useRefreshStripeConnectStatusForChamberMutation(settingsActionOptions);
  const [
    deleteStripeConnectForSandboxChamber,
    { loading: deletingStripeConnect },
  ] = useDeleteStripeConnectForSandboxChamberMutation(settingsActionOptions);
  const [
    startChamberMemberProSenderDomainVerification,
    { loading: startingSenderDomainVerification },
  ] = useStartChamberMemberProSenderDomainVerificationMutation(settingsActionOptions);
  const [
    refreshChamberMemberProSenderDomainStatusForChamber,
    { loading: refreshingSenderDomainStatus },
  ] = useRefreshChamberMemberProSenderDomainStatusForChamberMutation(settingsActionOptions);
  const [
    removeChamberMemberProSenderDomainForChamber,
    { loading: removingSenderDomain },
  ] = useRemoveChamberMemberProSenderDomainForChamberMutation(settingsActionOptions);
  const [
    sendChamberMemberProSenderDomainTestEmail,
    { loading: sendingSenderDomainTestEmail },
  ] = useSendChamberMemberProSenderDomainTestEmailMutation(settingsActionOptions);

  const applyMappedSettings = useCallback((
    mappedSettings: ChamberSettingsFormState,
    savedAtFallbackUtcMilli: number | null = null,
  ) => {
    pendingSettingsQueryRef.current = null;
    setSettings(mappedSettings);
    setLastSavedSettings(mappedSettings);
    setSavedAtUtcMilli(
      mappedSettings.updatedAtUtcMilli || savedAtFallbackUtcMilli
    );
    setSaveError(null);
    setSenderDomainError(null);
    setActiveSavingSection(null);
    setLastSavedSection(null);
    setLastFailedSection(null);
  }, []);

  const applySettingsFromApi = useCallback((
    apiSettings: Parameters<typeof mapApiSettingsToFormState>[0],
    savedAtFallbackUtcMilli: number | null = null
  ) => {
    applyMappedSettings(
      mapApiSettingsToFormState(apiSettings),
      savedAtFallbackUtcMilli,
    );
  }, [applyMappedSettings]);

  useEffect(() => {
    const apiSettings = settingsData?.getChamberMemberProSettings;
    if (!apiSettings || !chamberId) {
      return;
    }

    const incomingSettings = mapApiSettingsToFormState(apiSettings);
    const lastSeenQuery = lastSeenSettingsQueryRef.current;
    const queryChanged =
      !lastSeenQuery ||
      lastSeenQuery.chamberId !== chamberId ||
      hasUnsavedFormChanges({
        baseline: lastSeenQuery.settings,
        current: incomingSettings,
      });

    if (queryChanged) {
      const nextQuery = { chamberId, settings: incomingSettings };
      lastSeenSettingsQueryRef.current = nextQuery;
      pendingSettingsQueryRef.current = nextQuery;
    }

    const pendingQuery = pendingSettingsQueryRef.current;
    if (
      !pendingQuery ||
      pendingQuery.chamberId !== chamberId ||
      !shouldApplySettingsQueryRefresh({
        activeChamberId: lastSeenQuery?.chamberId || "",
        baseline: lastSavedSettings,
        current: settings,
        hasExternalUnsavedChanges: emailTemplateEditorDirty || internalEmailsDirty || emailSubscriberTargetDirty || governanceDraftDirty,
        incomingChamberId: pendingQuery.chamberId,
      })
    ) {
      return;
    }

    applyMappedSettings(pendingQuery.settings);
  }, [
    applyMappedSettings,
    chamberId,
    emailSubscriberTargetDirty,
    emailTemplateEditorDirty,
    internalEmailsDirty,
    governanceDraftDirty,
    lastSavedSettings,
    settings,
    settingsData,
  ]);

  useEffect(() => {
    const stripeConnectResult = searchParams.get("stripeConnect");

    if (stripeConnectResult === "success") {
      setActiveTab("globalSettings");
      setStatusNotice("Stripe connection completed successfully.");
      setStripeConnectError(null);
      return;
    }

    if (stripeConnectResult === "error") {
      setActiveTab("globalSettings");
      // The page shows fixed text, never text taken from the web address.
      setStripeConnectError(
        buildStaffErrorFallback("finish the Stripe connection")
      );
    }
  }, [searchParams]);

  const hasUnsavedChanges = useMemo(() => {
    if (!settings || !lastSavedSettings) {
      return false;
    }

    return (
      hasUnsavedFormChanges({
        baseline: lastSavedSettings,
        current: settings,
      }) ||
      emailTemplateEditorDirty || internalEmailsDirty || emailSubscriberTargetDirty || governanceDraftDirty
    );
  }, [emailTemplateEditorDirty, internalEmailsDirty, emailSubscriberTargetDirty, governanceDraftDirty, settings, lastSavedSettings]);

  const updateSettings = (
    updater: (currentSettings: ChamberSettingsFormState) => ChamberSettingsFormState
  ) => {
    setSettings((currentSettings) =>
      currentSettings ? updater(currentSettings) : currentSettings
    );
    setSaveError(null);
    setStatusNotice(null);
    setLastFailedSection(null);
  };

  const isSectionDirty = (sectionId: GlobalSettingsSectionId) => {
    if (!settings || !lastSavedSettings) {
      return false;
    }

    return hasUnsavedFormChanges({
      baseline: getSectionValue(lastSavedSettings, sectionId),
      current: getSectionValue(settings, sectionId),
    });
  };

  const handleSaveSection = async (sectionId: GlobalSettingsSectionId, template?: EmailTemplate) => {
    if (!settings || !lastSavedSettings || !chamberId) {
      return false;
    }

    // Design (11): the required-field message shows above the section buttons
    // only (no failure pop-up), and nothing is sent.
    if (
      sectionId === "chamberGlobals" &&
      getMissingChamberGlobalsFields(settings.setup).length > 0
    ) {
      setSaveError(CHAMBER_GLOBALS_REQUIRED_MESSAGE);
      setLastSavedSection(null);
      setLastFailedSection(sectionId);
      return false;
    }
    if (
      sectionId === "chamberGlobals" &&
      isRenewalFixedDateMissing(settings.setup)
    ) {
      setSaveError(RENEWAL_FIXED_DATE_REQUIRED_MESSAGE);
      setLastSavedSection(null);
      setLastFailedSection(sectionId);
      return false;
    }
    if (
      sectionId === "chamberGlobals" &&
      areMultipleBillingDatesMissing(settings.setup)
    ) {
      setSaveError(MULTIPLE_BILLING_DATES_REQUIRED_MESSAGE);
      setLastSavedSection(null);
      setLastFailedSection(sectionId);
      return false;
    }

    const scopedSettingsForSave = getSectionScopedSettings(
      lastSavedSettings,
      template ? { ...settings, email: { ...settings.email, emailTemplates: settings.email.emailTemplates.map(current => current.id === template.id ? template : current) } } : settings,
      sectionId,
      template?.id
    );

    try {
      setActiveSavingSection(sectionId);
      setLastSavedSection(null);
      setLastFailedSection(null);
      if (sectionId === "membershipSettings") {
        for (const model of scopedSettingsForSave.membership.membershipModels) {
          if (model.pricingModel !== "employeeRange") continue;
          const issue = getMembershipEmployeeRangeIssues(model.ranges)[0];
          if (issue) throw new UserFacingError(`${model.name}: ${issue.message}`);
          if (!model.ranges.length) {
            throw new UserFacingError(`${model.name}: Add at least one employee range.`);
          }
        }
      }
      const result = await updateChamberMemberProSettings({
        context: settingsOperationErrorContext,
        variables: {
          chamberId,
          chamberMemberProSettings:
            mapFormStateToApiSettingsInput(scopedSettingsForSave),
          section: getSettingsSectionForSave(sectionId),
          templateId: template?.id,
        },
      });

      const savedSettings =
        result.data?.updateChamberMemberProSettingsForChamber
          .chamberMemberProSettings;

      if (!savedSettings) {
        throw new Error("Settings update returned no data");
      }

      const mappedSavedSettings = mapApiSettingsToFormState(savedSettings);
      // IF13 Q-A: a new copy address from Invoicing Options is saved to its
      // Internal Emails card, which decides who receives the copies.
      if (sectionId === "invoicingOptions" && invoiceCopyCards) {
        const cardUpdates = getInvoiceCopyCardUpdates(mappedSavedSettings.paymentsAndTaxes, invoiceCopyCards);
        for (const input of cardUpdates) {
          await updateInternalEmailSetting({ variables: { input: { chamberId, ...input } } });
        }
        if (cardUpdates.length) await refetchInternalEmails();
      }
      pendingSettingsQueryRef.current = null;
      // Patch the same section in the form and its baseline. Unrelated
      // response defaults or derived fields must not create unsaved changes.
      setLastSavedSettings((currentBaseline) =>
        currentBaseline
          ? getSectionScopedSettings(
              currentBaseline,
              mappedSavedSettings,
              sectionId,
              template?.id
            )
          : mappedSavedSettings
      );
      setSettings((currentSettings) =>
        currentSettings
          ? getSectionScopedSettings(
              currentSettings,
              mappedSavedSettings,
              sectionId,
              template?.id
            )
          : mappedSavedSettings
      );
      setSavedAtUtcMilli(mappedSavedSettings.updatedAtUtcMilli || Date.now());
      setSaveError(null);
      setLastSavedSection(sectionId);
      setLastFailedSection(null);
      // Automated email templates show their own design success pop-up.
      if (!template) {
        setSectionSaveFeedback({
          message: "Your changes have been saved successfully!",
          tone: "success",
        });
      }
      return true;
    } catch (error: unknown) {
      setSaveError(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback(`save the ${noun.singular} settings`)
        )
      );
      setLastFailedSection(sectionId);
      setSectionSaveFeedback({
        message: SECTION_SAVE_FAILED_MESSAGE,
        tone: "error",
      });
      return false;
    } finally {
      setActiveSavingSection(null);
    }
  };

  const saveChangedSections = async () => {
    if (saving || affinityBusy || affinityDirty || !settings) return false;
    for (const sectionId of globalSettingsSectionIds.filter(isSectionDirty)) {
      if (!(await handleSaveSection(sectionId))) return false;
    }
    return true;
  };

  const handleResetSection = (sectionId: GlobalSettingsSectionId) => {
    if (!lastSavedSettings) {
      return;
    }

    setSettings((currentSettings) =>
      currentSettings
        ? getSectionScopedSettings(
            currentSettings,
            lastSavedSettings,
            sectionId
          )
        : currentSettings
    );
    setSaveError(null);
    setStatusNotice(null);
    setLastSavedSection(null);
    setLastFailedSection(null);
  };

  const handleStartStripeConnect = async () => {
    if (!chamberId) {
      return;
    }

    if (hasUnsavedChanges) {
      setStripeConnectError(
        UNSAVED_BEFORE_STRIPE_TEXT
      );
      return;
    }

    try {
      setStripeConnectError(null);
      setStatusNotice(null);

      const result = await startStripeConnectForChamber({
        variables: { chamberId },
      });

      const response = result.data?.startStripeConnectForChamber;

      if (!response?.chamberMemberProSettings) {
        throw new Error("Stripe Connect setup returned no settings");
      }

      applySettingsFromApi(response.chamberMemberProSettings, Date.now());

      if (response.stripeConnectOAuthUrl) {
        window.location.href = response.stripeConnectOAuthUrl;
        return;
      }

      setStatusNotice("Stripe connection updated.");
    } catch (error: unknown) {
      setStripeConnectError(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback("start the Stripe Connect setup")
        )
      );
    }
  };

  const handleRefreshStripeConnect = async () => {
    if (!chamberId) {
      return;
    }

    if (hasUnsavedChanges) {
      setStripeConnectError(
        UNSAVED_BEFORE_STRIPE_TEXT
      );
      return;
    }

    try {
      setStripeConnectError(null);
      setStatusNotice(null);

      const result = await refreshStripeConnectStatusForChamber({
        variables: { chamberId },
      });

      const refreshedSettings =
        result.data?.refreshStripeConnectStatusForChamber
          .chamberMemberProSettings;

      if (!refreshedSettings) {
        throw new Error("Stripe Connect refresh returned no settings");
      }

      applySettingsFromApi(refreshedSettings, Date.now());
      setStatusNotice("Stripe connection status refreshed.");
    } catch (error: unknown) {
      setStripeConnectError(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback("refresh the Stripe Connect status")
        )
      );
    }
  };

  const handleDeleteStripeConnect = () => {
    if (!chamberId) {
      return;
    }

    if (hasUnsavedChanges) {
      setStripeConnectError(
        UNSAVED_BEFORE_STRIPE_TEXT
      );
      return;
    }

    setStripeDisconnectConfirmOpen(true);
  };

  const confirmDeleteStripeConnect = async () => {
    if (!chamberId) {
      return;
    }

    try {
      setStripeConnectError(null);
      setStatusNotice(null);

      const result = await deleteStripeConnectForSandboxChamber({
        variables: { chamberId },
      });

      const deletedSettings =
        result.data?.deleteStripeConnectForSandboxChamber
          .chamberMemberProSettings;

      if (!deletedSettings) {
        throw new Error("Stripe Connect delete returned no settings");
      }

      applySettingsFromApi(deletedSettings, Date.now());
      setStatusNotice("Stripe test connection deleted.");
    } catch (error: unknown) {
      setStripeConnectError(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback("delete the Stripe test connection")
        )
      );
    } finally {
      // Close in a finally so an error still dismisses the dialog and the
      // Stripe section error banner takes over.
      setStripeDisconnectConfirmOpen(false);
    }
  };

  const handleStartSenderDomainVerification = async ({
    domain,
    senderEmail,
  }: {
    domain: string;
    senderEmail: string;
  }) => {
    if (!chamberId) {
      return;
    }

    if (hasUnsavedChanges) {
      setSenderDomainError(
        UNSAVED_BEFORE_EMAIL_DOMAIN_TEXT
      );
      return;
    }

    try {
      setSenderDomainError(null);
      setStatusNotice(null);

      const result = await startChamberMemberProSenderDomainVerification({
        variables: { chamberId, domain, senderEmail },
      });
      const updatedSettings =
        result.data?.startChamberMemberProSenderDomainVerification
          .chamberMemberProSettings;

      if (!updatedSettings) {
        throw new Error("Email domain setup returned no settings");
      }

      applySettingsFromApi(updatedSettings, Date.now());
      setStatusNotice("Email domain setup started.");
    } catch (error: unknown) {
      setSenderDomainError(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback("start the email domain setup")
        )
      );
    }
  };

  const handleRefreshSenderDomainStatus = async () => {
    if (!chamberId) {
      return;
    }

    if (hasUnsavedChanges) {
      setSenderDomainError(
        UNSAVED_BEFORE_EMAIL_DOMAIN_TEXT
      );
      return;
    }

    try {
      setSenderDomainError(null);
      setStatusNotice(null);

      const result =
        await refreshChamberMemberProSenderDomainStatusForChamber({
          variables: { chamberId },
        });
      const updatedSettings =
        result.data?.refreshChamberMemberProSenderDomainStatusForChamber
          .chamberMemberProSettings;

      if (!updatedSettings) {
        throw new Error("Email domain status check returned no settings");
      }

      applySettingsFromApi(updatedSettings, Date.now());
      setStatusNotice("Email domain status checked.");
    } catch (error: unknown) {
      setSenderDomainError(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback("check the email domain status")
        )
      );
    }
  };

  const handleRemoveSenderDomain = async () => {
    if (!chamberId) {
      return;
    }

    if (hasUnsavedChanges) {
      setSenderDomainError(
        UNSAVED_BEFORE_EMAIL_DOMAIN_TEXT
      );
      return;
    }

    setRemoveSenderDomainConfirmOpen(true);
  };

  const confirmRemoveSenderDomain = async () => {
    if (!chamberId) {
      return;
    }

    try {
      setSenderDomainError(null);
      setStatusNotice(null);

      const result = await removeChamberMemberProSenderDomainForChamber({
        variables: { chamberId },
      });
      const updatedSettings =
        result.data?.removeChamberMemberProSenderDomainForChamber
          .chamberMemberProSettings;

      if (!updatedSettings) {
        throw new Error("Email domain removal returned no settings");
      }

      applySettingsFromApi(updatedSettings, Date.now());
      setStatusNotice("Email domain removed.");
    } catch (error: unknown) {
      setSenderDomainError(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback("remove the email domain")
        )
      );
    } finally {
      // Close in a finally so an error still dismisses the dialog and the
      // section error banner (senderDomainError) takes over.
      setRemoveSenderDomainConfirmOpen(false);
    }
  };

  const handleSendSenderDomainTestEmail = async (recipientEmail: string) => {
    if (!chamberId) {
      return;
    }

    if (hasUnsavedChanges) {
      setSenderDomainError(
        UNSAVED_BEFORE_EMAIL_DOMAIN_TEXT
      );
      return;
    }

    try {
      setSenderDomainError(null);
      setStatusNotice(null);

      const result = await sendChamberMemberProSenderDomainTestEmail({
        variables: { chamberId, recipientEmail },
      });
      const updatedSettings =
        result.data?.sendChamberMemberProSenderDomainTestEmail
          .chamberMemberProSettings;

      if (!updatedSettings) {
        throw new Error("Test email returned no settings");
      }

      applySettingsFromApi(updatedSettings, Date.now());
      setStatusNotice("Test email sent.");
    } catch (error: unknown) {
      setSenderDomainError(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback("send the test email")
        )
      );
    }
  };

  // Returning from a tab without settings resumes the network-only query.
  // Keep this chamber's loaded form mounted so child drafts and their guards survive.
  const hasLoadedCurrentChamberSettings = Boolean(
    settings && lastSeenSettingsQueryRef.current?.chamberId === chamberId
  );
  const canUseLoadedSettings = hasLoadedCurrentChamberSettings &&
    blockedSettingsChamberRef.current !== chamberId;
  const loading =
    !loggedInUser ||
    (visibleTabItems.length > 0 && !activeTabIsVisible) ||
    (shouldLoadSettings && settingsLoading && !canUseLoadedSettings);
  const loadError = shouldLoadSettings ? settingsError : undefined;
  const keepLoadedSettingsOnError = Boolean(
    canUseLoadedSettings &&
    loadError &&
    isRecoverableSettingsNetworkError(loadError)
  );
  const handleRetrySettings = async () => {
    try {
      await refetch();
    } catch {
      // The query supplies the retry error below. The existing Apollo context
      // also reports unexpected failures; do not create an unhandled rejection.
    }
  };
  const loadErrorNotice = loadError ? (
    <div role="alert" className="mx-auto max-w-3xl rounded-lg border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
      <p className="font-semibold">
        {keepLoadedSettingsOnError
          ? `Unable to refresh ${noun.singular} settings.`
          : `Unable to load ${noun.singular} settings.`}
      </p>
      <p className="mt-1">
        {getUserFacingErrorText(
          loadError,
          buildStaffErrorFallback(`load the ${noun.singular} settings`)
        )}
      </p>
      {keepLoadedSettingsOnError ? (
        <p className="mt-1">Your unsaved changes are still on this page.</p>
      ) : null}
      <button
        type="button"
        onClick={handleRetrySettings}
        disabled={settingsLoading}
        className="mt-4 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Try again
      </button>
    </div>
  ) : null;

  if (loggedInUser && visibleTabItems.length === 0) {
    return (
      <div className="mx-auto max-w-3xl rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800">
        {`You do not have access to ${applyCmpOrganizationNounToPageName("Chamber Set Up", noun)}. Ask your ${noun.singular} administrator to give you access in ${applyCmpOrganizationNounToPageName("Chamber Set Up", noun)} > Create Employees and Permissions.`}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-[360px] items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (loadError && !keepLoadedSettingsOnError) {
    return loadErrorNotice;
  }

  if (!chamberId) {
    return (
      <div className="mx-auto max-w-3xl rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800">
        {`Your sign-in is not connected to a ${noun.singular}, so ${applyCmpOrganizationNounToPageName("Chamber Set Up", noun)} cannot open. Email ${STAFF_SUPPORT_EMAIL} for help.`}
      </div>
    );
  }

  if (!settings && activeTab === "usersPermissions") {
    return (
      <div>
        <SettingsTabs
          activeTab={activeTab}
          items={visibleTabItems}
          onTabChange={handleTabChange}
        />
        <div className="mt-[24px] w-full">
          <UsersPermissionsTab
            accessMode={isOwner ? "owner" : "activity-only"}
            initialCreationFeedback={staffUserCreationFeedback}
            timeZone={loggedInUser?.chamberMemberProTimeZone || undefined}
          />
        </div>
      </div>
    );
  }

  if (!settings && activeTab === "affinityProgramSetup") {
    return (
      <div>
        <UnsavedChangesPrompt when={affinityDirty} saving={affinityBusy} />
        <SettingsTabs
          activeTab={activeTab}
          items={visibleTabItems}
          onTabChange={handleTabChange}
        />
        <div className="mt-[24px] w-full">
          <AffinityProgramSetupTab
            chamberId={chamberId}
            lastSavedLabel={formatSavedAt(savedAtUtcMilli, chamberTimeZone)}
            timeZone={chamberTimeZone}
            onDirtyChange={setAffinityDirty}
            onBusyChange={setAffinityBusy}
          />
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="mx-auto max-w-3xl rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800">
        {`${applyCmpOrganizationNounToPageName("Chamber Set Up", noun)} could not open. Refresh the page. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`}
      </div>
    );
  }

  const subscriberTargets =
    settingsData?.getChamberMemberProSettings.email.subscriberTargets || [];
  const currentCalendarYear = Number(
    new Intl.DateTimeFormat("en-US", {
      timeZone: normalizeCmpTimeZone(
        settingsData?.getChamberMemberProSettings.setup.timeZone
      ).timeZone,
      year: "numeric",
    }).format(new Date())
  );
  const currentEmailSubscriberTarget =
    subscriberTargets.find(
      (target) => target.calendarYear === currentCalendarYear
    ) || null;

  if (
    activeTab === "emailCommunications" &&
    !currentEmailSubscriberTarget
  ) {
    return (
      <div className="mx-auto max-w-3xl rounded-lg border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
        {`We could not load this year's email subscriber target, so Automated Emails cannot open. Refresh the page. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`}
      </div>
    );
  }

  const makeSectionControls = (
    sectionId: GlobalSettingsSectionId
  ) => ({
    isDirty: isSectionDirty(sectionId),
    saving: saving && activeSavingSection === sectionId,
    lastSavedLabel: formatSavedAt(savedAtUtcMilli, chamberTimeZone),
    onSave: async () => { await handleSaveSection(sectionId); },
    onReset: () => handleResetSection(sectionId),
    statusText:
      lastSavedSection === sectionId && statusNotice ? statusNotice : null,
    errorText:
      lastFailedSection === sectionId
        ? saveError
        : null,
  });
  const sectionControls = {
    chamberGlobals: makeSectionControls("chamberGlobals"),
    taxSettings: makeSectionControls("taxSettings"),
    accountAllocation: makeSectionControls("accountAllocation"),
    paymentOptions: makeSectionControls("paymentOptions"),
    invoicingOptions: makeSectionControls("invoicingOptions"),
    memberCommunication: makeSectionControls("memberCommunication"),
    membershipSettings: makeSectionControls("membershipSettings"),
    emailCommunications: makeSectionControls("emailCommunications"),
    governanceGroups: makeSectionControls("governanceGroups"),
  };

  // Design (11) Chamber Set Up leave guard (CMP Dashboard 4026-4042, 14754):
  // switching tabs with unsaved changes asks first. Save Changes saves the
  // changed sections; Leave restores the saved settings, then switches.
  const requestTabChange = (nextTab: TabId) => {
    if (nextTab !== activeTab && (hasUnsavedChanges || affinityDirty)) {
      setPendingTab(nextTab);
      return;
    }
    handleTabChange(nextTab);
  };
  const leaveToPendingTab = () => {
    if (!pendingTab) return;
    if (lastSavedSettings) setSettings(lastSavedSettings);
    setSaveError(null);
    setLastFailedSection(null);
    handleTabChange(pendingTab);
    setPendingTab(null);
  };
  const saveAndGoToPendingTab = async () => {
    if (!pendingTab) return;
    if (await saveChangedSections()) {
      handleTabChange(pendingTab);
      setPendingTab(null);
      return;
    }
    // Design (16): a save stopped by validation keeps staff on this tab with
    // the errors shown.
    setPendingTab(null);
  };

  return (
    <div>
      <UnsavedChangesPrompt
        message={CHAMBER_SET_UP_UNSAVED_MESSAGE}
        when={(hasUnsavedChanges || affinityDirty) && !pendingTab}
        saving={saving || affinityBusy || emailSubscriberTargetBusy}
        onSave={affinityDirty ? undefined : saveChangedSections}
        saveError={saveError || undefined}
      />
      {pendingTab ? (
        <UnsavedChangesDialog
          message={CHAMBER_SET_UP_UNSAVED_MESSAGE}
          onLeave={leaveToPendingTab}
          onSave={affinityDirty ? undefined : () => void saveAndGoToPendingTab()}
          onStay={() => setPendingTab(null)}
          saving={saving || affinityBusy || emailSubscriberTargetBusy}
          saveError={saveError || undefined}
        />
      ) : null}
      <SettingsTabs
        activeTab={activeTab}
        items={visibleTabItems}
        onTabChange={requestTabChange}
      />

      {loadErrorNotice ? <div className="mt-6">{loadErrorNotice}</div> : null}

      <div className="mt-[24px] w-full">
        {visibleTabIds.includes("globalSettings") ? (
          <div className={activeTab === "globalSettings" ? "block" : "hidden"}>
            <GlobalSettingsTab
              settings={settings}
              chamberContactEmail={loggedInUser?.chamberInfo?.supportContactEmail || ""}
              chamberWebsiteUrl={loggedInUser?.chamberInfo?.chamberMemberProWebsiteUrl}
              onOpenAutomatedEmails={visibleTabIds.includes("emailCommunications")
                ? () => handleTabChange("emailCommunications") : undefined}
              isSandboxChamber={Boolean(loggedInUser?.chamberInfo?.isSandbox)}
              onChange={(nextSettings) => updateSettings(() => nextSettings)}
              sectionControls={sectionControls}
              onStartStripeConnect={handleStartStripeConnect}
              onRefreshStripeConnect={handleRefreshStripeConnect}
              onDeleteStripeConnect={handleDeleteStripeConnect}
              stripeConnectActionLoading={startingStripeConnect}
              stripeConnectRefreshLoading={refreshingStripeConnect}
              stripeConnectDeleteLoading={deletingStripeConnect}
              stripeConnectError={stripeConnectError}
              onStartSenderDomainVerification={
                handleStartSenderDomainVerification
              }
              onRefreshSenderDomainStatus={handleRefreshSenderDomainStatus}
              onRemoveSenderDomain={handleRemoveSenderDomain}
              onSendSenderDomainTestEmail={handleSendSenderDomainTestEmail}
              senderDomainActionLoading={startingSenderDomainVerification}
              senderDomainRefreshLoading={refreshingSenderDomainStatus}
              senderDomainRemoveLoading={removingSenderDomain}
              senderDomainTestEmailLoading={sendingSenderDomainTestEmail}
              senderDomainError={senderDomainError}
              hasUnsavedSettingsChanges={hasUnsavedChanges}
            />
          </div>
        ) : null}
        {visibleTabIds.includes("membershipSettings") ? (
          <div
            className={
              activeTab === "membershipSettings" ? "block" : "hidden"
            }
          >
            <MembershipTab
              settings={settings.membership}
              prorationSetup={settings.setup}
              accountAllocations={settings.paymentsAndTaxes.accountAllocations}
              sectionControls={sectionControls.membershipSettings}
              onChange={(membership) =>
                updateSettings((currentSettings) => ({
                  ...currentSettings,
                  membership,
                }))
              }
            />
          </div>
        ) : null}
        {visibleTabIds.includes("emailCommunications") &&
        currentEmailSubscriberTarget ? (
          <div
            className={
              activeTab === "emailCommunications" ? "block" : "hidden"
            }
          >
            <EmailTab
              chamberId={chamberId}
              timeZone={normalizeCmpTimeZone(settings.setup.timeZone).timeZone}
              settings={settings.email}
              savedSettings={lastSavedSettings?.email || settings.email}
              onInternalEmailsDirtyChange={setInternalEmailsDirty}
              onInternalEmailSaved={() => { refetchInternalEmails().catch(() => undefined); }}
              saving={saving}
              onSaveTemplate={(template) => handleSaveSection("emailCommunications", template)}
              onEditorDirtyChange={setEmailTemplateEditorDirty}
              subscriberTarget={currentEmailSubscriberTarget}
              onSubscriberTargetDirtyChange={setEmailSubscriberTargetDirty}
              onSubscriberTargetBusyChange={setEmailSubscriberTargetBusy}
              onChange={(email) =>
                updateSettings((currentSettings) => ({
                  ...currentSettings,
                  email,
                }))
              }
            />
          </div>
        ) : null}
        {visibleTabIds.includes("affinityProgramSetup") ? (
          <div
            className={
              activeTab === "affinityProgramSetup" ? "block" : "hidden"
            }
          >
            <AffinityProgramSetupTab
              chamberId={chamberId}
              lastSavedLabel={formatSavedAt(savedAtUtcMilli, chamberTimeZone)}
            timeZone={chamberTimeZone}
              onDirtyChange={setAffinityDirty}
              onBusyChange={setAffinityBusy}
            />
          </div>
        ) : null}
        {visibleTabIds.includes("governanceGroups") ? (
          <div className={activeTab === "governanceGroups" ? "block" : "hidden"}>
            <GovernanceGroupsTab
              key={chamberId}
              settings={settings.governance}
              sectionControls={sectionControls.governanceGroups}
              onDirtyChange={setGovernanceDraftDirty}
              onChange={(governance: ChamberSettingsFormState["governance"]) => updateSettings((currentSettings) => ({
                ...currentSettings,
                governance,
              }))}
            />
          </div>
        ) : null}
        {visibleTabIds.includes("usersPermissions") ? (
          <div
            className={
              activeTab === "usersPermissions" ? "block" : "hidden"
            }
          >
            <UsersPermissionsTab
              accessMode={isOwner ? "owner" : "activity-only"}
              initialCreationFeedback={staffUserCreationFeedback}
              timeZone={
                loggedInUser?.chamberMemberProTimeZone ||
                settings.setup.timeZone
              }
            />
          </div>
        ) : null}
      </div>

      <ActionSuccessDialog
        message={sectionSaveFeedback?.message}
        tone={sectionSaveFeedback?.tone}
        onClose={() => setSectionSaveFeedback(null)}
      />
      <ConfirmDialog
        open={stripeDisconnectConfirmOpen}
        variant="destructive"
        title="Delete the Stripe test connection?"
        message="This disconnects the test Stripe account from Chamber Member Pro. To take card payments again, connect a Stripe account again."
        loading={deletingStripeConnect}
        onConfirm={confirmDeleteStripeConnect}
        onClose={() => setStripeDisconnectConfirmOpen(false)}
      />
      <ConfirmDialog
        open={removeSenderDomainConfirmOpen}
        variant="destructive"
        title="Remove your email domain setup?"
        message="Emails will stop sending from your own email domain. Your settings at your domain provider (for example GoDaddy) will not change."
        loading={removingSenderDomain}
        onConfirm={confirmRemoveSenderDomain}
        onClose={() => setRemoveSenderDomainConfirmOpen(false)}
      />
    </div>
  );
};

export default ChamberSettings;
